/**
 * Google Analytics 4 Event Tracking Utility
 * 
 * This module provides helper functions for tracking user interactions
 * and e-commerce events in Google Analytics 4.
 */

import { isGoogleAdsActive, isMetaActive, isTikTokActive, adPixelsConfig } from './adPixelsConfig';

export interface GAProduct {
  item_id: string;
  item_name: string;
  item_category?: string;
  item_category2?: string;
  price: number;
  quantity: number;
  item_brand?: string;
  item_variant?: string;
}

export interface GAEcommerceEvent {
  currency?: string;
  value?: number;
  items: GAProduct[];
  transaction_id?: string;
  shipping?: number;
  tax?: number;
  coupon?: string;
}

/**
 * Track a Google Analytics 4 event
 */
export const trackGAEvent = (eventName: string, params: any = {}) => {
  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
    try {
      window.gtag('event', eventName, params);
      console.log('[GA4 Event]', eventName, params);
    } catch (error) {
      console.error('[GA4 Error]', eventName, error);
    }
  } else {
    console.warn('[GA4] gtag not available');
  }
};

/**
 * Format product data for GA4 events
 */
export const formatGAProduct = (product: any, quantity: number = 1): GAProduct => {
  return {
    item_id: product.id?.toString() || '',
    item_name: product.name || 'Unknown Product',
    item_category: product.categories?.name || product.category_name || 'Uncategorized',
    item_category2: product.subcategories?.name || product.subcategory_name || undefined,
    price: product.price_per_unit || product.price || 0,
    quantity: quantity,
    item_brand: product.supplier_name || 'Excess Market',
    item_variant: product.material || product.color_family || undefined,
  };
};

/**
 * Format cart items for GA4 events
 */
export const formatCartItems = (items: any[]): GAProduct[] => {
  return items.map(item => ({
    item_id: item.id?.toString() || '',
    item_name: item.name || 'Unknown Product',
    item_category: item.category || 'Uncategorized',
    price: item.price || 0,
    quantity: item.quantity || 1,
  }));
};

/**
 * Track product view
 */
export const trackProductView = (product: any) => {
  trackGAEvent('view_item', {
    currency: 'ILS',
    value: product.price_per_unit || product.price || 0,
    items: [formatGAProduct(product, 1)]
  });
};

/**
 * Track product list view
 */
export const trackProductListView = (products: any[], listName: string) => {
  trackGAEvent('view_item_list', {
    item_list_name: listName,
    items: products.slice(0, 10).map((product, index) => ({
      ...formatGAProduct(product, 1),
      index: index
    }))
  });
};

/**
 * Track add to cart
 */
export const trackAddToCart = (product: any, quantity: number, totalValue: number) => {
  trackGAEvent('add_to_cart', {
    currency: 'ILS',
    value: totalValue,
    items: [formatGAProduct(product, quantity)]
  });
};

/**
 * Track remove from cart
 */
export const trackRemoveFromCart = (product: any, quantity: number) => {
  trackGAEvent('remove_from_cart', {
    currency: 'ILS',
    value: (product.price || 0) * quantity,
    items: [formatGAProduct(product, quantity)]
  });
};

/**
 * Track view cart
 */
export const trackViewCart = (items: any[], totalValue: number) => {
  trackGAEvent('view_cart', {
    currency: 'ILS',
    value: totalValue,
    items: formatCartItems(items)
  });
};

/**
 * Track begin checkout
 */
export const trackBeginCheckout = (items: any[], totalValue: number) => {
  trackGAEvent('begin_checkout', {
    currency: 'ILS',
    value: totalValue,
    items: formatCartItems(items)
  });
};

/**
 * Track add shipping info
 */
export const trackAddShippingInfo = (items: any[], totalValue: number, shippingTier?: string) => {
  trackGAEvent('add_shipping_info', {
    currency: 'ILS',
    value: totalValue,
    shipping_tier: shippingTier,
    items: formatCartItems(items)
  });
};

/**
 * Track add payment info
 */
export const trackAddPaymentInfo = (items: any[], totalValue: number, paymentType: string) => {
  trackGAEvent('add_payment_info', {
    currency: 'ILS',
    value: totalValue,
    payment_type: paymentType,
    items: formatCartItems(items)
  });
};

/**
 * Track purchase
 */
export const trackPurchase = (params: {
  transaction_id: string;
  value: number;
  shipping?: number;
  tax?: number;
  items: any[];
}) => {
  // Ensure purchase is only tracked once per transaction
  const trackedTransactions = sessionStorage.getItem('tracked_purchases') || '[]';
  const transactions = JSON.parse(trackedTransactions);
  
  if (transactions.includes(params.transaction_id)) {
    console.log('[GA4] Purchase already tracked for transaction:', params.transaction_id);
    return;
  }
  
  trackGAEvent('purchase', {
    transaction_id: params.transaction_id,
    value: params.value,
    currency: 'ILS',
    tax: params.tax || 0,
    shipping: params.shipping || 0,
    items: formatCartItems(params.items)
  });
  
  // Mark transaction as tracked
  transactions.push(params.transaction_id);
  sessionStorage.setItem('tracked_purchases', JSON.stringify(transactions));
};

/**
 * Track search
 */
export const trackSearch = (searchTerm: string, resultsCount?: number) => {
  trackGAEvent('search', {
    search_term: searchTerm,
    results_count: resultsCount
  });
};

/**
 * Track add to wishlist
 */
export const trackAddToWishlist = (product: any) => {
  trackGAEvent('add_to_wishlist', {
    currency: 'ILS',
    value: product.price_per_unit || product.price || 0,
    items: [formatGAProduct(product, 1)]
  });
};

/**
 * Track remove from wishlist
 */
export const trackRemoveFromWishlist = (product: any) => {
  trackGAEvent('remove_from_wishlist', {
    currency: 'ILS',
    value: product.price_per_unit || product.price || 0,
    items: [formatGAProduct(product, 1)]
  });
};

/**
 * Track share
 */
export const trackShare = (itemId: string, method: string, contentType: string = 'product') => {
  trackGAEvent('share', {
    method: method,
    content_type: contentType,
    item_id: itemId
  });
};

/**
 * Track sign up
 */
export const trackSignUp = (method: string = 'email') => {
  trackGAEvent('sign_up', {
    method: method
  });
};

/**
 * Track login
 */
export const trackLogin = (method: string = 'email') => {
  trackGAEvent('login', {
    method: method
  });
};

/**
 * Track view order
 */
export const trackViewOrder = (orderId: string, value: number) => {
  trackGAEvent('view_order', {
    order_id: orderId,
    value: value,
    currency: 'ILS'
  });
};

/**
 * Ad Pixel Fan-out Helpers
 * These functions send events to Meta, TikTok, and Google Ads when enabled
 */

function trackMeta(event: string, params?: Record<string, unknown>) {
  if (!isMetaActive() || typeof window.fbq !== 'function') return;
  window.fbq('track', event, params || {});
}

function trackTikTok(event: string, params?: Record<string, unknown>) {
  if (!isTikTokActive() || !window.ttq?.track) return;
  window.ttq.track(event, params || {});
}

/** Call on confirmed purchase — maps GA purchase → Meta Purchase / TikTok CompletePayment / Ads conversion */
export const trackAdPurchase = (params: {
  transaction_id: string;
  value: number;
  currency?: string;
  contents?: { id: string; quantity: number; item_price?: number }[];
}) => {
  const currency = params.currency || 'ILS';
  const eventId = params.transaction_id;

  trackMeta('Purchase', {
    value: params.value,
    currency,
    contents: params.contents,
    content_type: 'product',
    eventID: eventId,
  });

  trackTikTok('CompletePayment', {
    value: params.value,
    currency,
    contents: params.contents,
    content_type: 'product',
    event_id: eventId,
  });

  if (isGoogleAdsActive() && typeof window.gtag === 'function') {
    const sendTo = adPixelsConfig.googleAds.purchaseLabel
      ? adPixelsConfig.googleAds.purchaseLabel
      : adPixelsConfig.googleAds.id;
    window.gtag('event', 'conversion', {
      send_to: sendTo,
      value: params.value,
      currency,
      transaction_id: params.transaction_id,
    });
  }
};

export const trackAdAddToCart = (value: number, contentId: string, quantity: number) => {
  trackMeta('AddToCart', { value, currency: 'ILS', content_ids: [contentId], content_type: 'product' });
  trackTikTok('AddToCart', { value, currency: 'ILS', contents: [{ content_id: contentId, quantity }] });
};

export const trackAdViewContent = (value: number, contentId: string) => {
  trackMeta('ViewContent', { value, currency: 'ILS', content_ids: [contentId], content_type: 'product' });
  trackTikTok('ViewContent', { value, currency: 'ILS', contents: [{ content_id: contentId, quantity: 1 }] });
};

export const trackAdInitiateCheckout = (value: number) => {
  trackMeta('InitiateCheckout', { value, currency: 'ILS' });
  trackTikTok('InitiateCheckout', { value, currency: 'ILS' });
};
